import { resourcesSubscriber } from '@/mqtt/resources'
import { getMessageSubscriber } from '@/store/modules/nextback/nextback.websocket.subscriber'
import SlideApi from '@/api/slide'
import SlideUtil from '@/utils/slide.utils'
import TimeUtil from '@/utils/time.utils'

const NEXTBACK_SET_PRESENTING = 'nextback/setPresenting'

const _state = {
  slideMap: {},
  /**
   * Separate state for quizStage
   * so that the data can be synced independently with slide data
   */
  quizStage: {},
  editor: null,
  slideList: [],
  activeSlideId: null,
}

const actions = {
  subscribeSlideChangeAction: ({ commit, rootGetters }, { presentation, editor }) => {
    /**
     * Consolidate the slide change to this module and only subscribe for slide id
     */
    if (presentation.audiencePacing) return
    if (!presentation.id) return
    commit('updateEditor', editor)
    resourcesSubscriber.subscribeResourceUri(`/LIVE_SESSION/${presentation.id}/ACTIVE_ACTIVITY`, async () => {
      /**
       * Ignore this msg from liveSession since now we are using aha-sync
       * TODO: remove this after staging stable
       */
      // const parsedMessage = JSON.parse(message)
      if (rootGetters['featureToggles/canUseNewChangeSlideMechanism']) {
        // dispatch('nextSlideAction', { editor, message: parsedMessage })
      }
    }, { once: true })
  },

  unsubscribeSlideChangeAction: (_, payload) => {
    resourcesSubscriber.unsubscribeResourceUri(`/LIVE_SESSION/${payload.id}/ACTIVE_ACTIVITY`)
  },

  /**
   * simplified interface for nextSlideAction
   * this does not contain quizStage logic, so don't use it
   * when move to next quiz slide with quizStage = 2
   */
  moveToSlideAction: ({ state, dispatch }, { slideId }) => {
    dispatch('nextSlideAction', { editor: state.editor, message: { activeSlide: slideId } })
  },

  /**
   * Identical to nextSlideAction, but do not require editor param
   */
  nextSlideActionV2: ({ dispatch, state }, { message }) => {
    dispatch('nextSlideAction', { editor: state.editor, message })
  },

  /**
   * @deprecated
   * @returns
   */
  nextSlideAction: ({ dispatch, commit }, { editor, message }) => {
    const slideId = message.activeSlide
    commit('setActiveSlideId', slideId)
    dispatch('prefetchNextSlideAction')
    const doc = {
      _from: 'live',
      type: 'presentation',
      action: 'nextSlide',
      data: { ...message, id: slideId },
      socketId: message.socketId,
    }

    if (message.quizStatus) {
      commit('updateQuizStage', doc.data)
    }

    const handler = getMessageSubscriber(doc.type)
    if (handler) {
      handler(doc, editor)
    }
  },

  prefetchSlidesAction: async ({ dispatch }, slideList) => {
    const delayInMs = 1000
    await slideList.reduce(async (promise, item) => {
      await dispatch('getSlideAction', item.id)
      await TimeUtil.sleep(delayInMs)
    }, Promise.resolve())
  },

  prefetchNextSlideAction: async ({ state, dispatch }) => {
    const currentIndex = state.slideList.indexOf(state.activeSlideId)
    if (currentIndex < state.slideList.length - 1) {
      const nextSlideId = state.slideList[currentIndex + 1]
      await dispatch('getSlideAction', nextSlideId)
    }
  },

  fetchSlideAction: async (_, slideId) => {
    const slide = await SlideApi.getSlideDetailV2(slideId)
    if (!slide.id) return null
    if (slide.canvasBlocksUrl) {
      const response = await fetch(slide.canvasBlocksUrl)
      const body = await response.json()
      slide.canvasBlocks = body
    }
    return SlideUtil.formatSlide(slide)
  },

  getSlideAction: async ({ state, dispatch }, slideId) => {
    const slideData = await dispatch('getSlideDataAction', slideId)
    if (slideId in state.quizStage) {
      const quizStage = JSON.parse(JSON.stringify(state.quizStage[slideId]))
      return { ...slideData, ...quizStage }
    }
    return slideData
  },

  getSlideDataAction: async ({ state, commit, dispatch }, slideId) => {
    if (slideId in state.slideMap) {
      return JSON.parse(JSON.stringify(state.slideMap[slideId]))
    }
    const slide = await dispatch('fetchSlideAction', slideId)
    commit('updateSlideMap', slide)
    return JSON.parse(JSON.stringify(slide))
  },

  syncActiveSlideAction: async ({ dispatch }, sessionId) => {
    dispatch('kvSync/subscribeKeyAction', {
      key: `/activeSlide/${sessionId}`,
    }, { root: true })
  },

  syncQuizStageAction: async ({ dispatch }, sessionId) => {
    dispatch('kvSync/subscribeKeyAction', {
      key: `/quizStage/${sessionId}`,
    }, { root: true })
  },

  changeQuizStageAction: async ({ state, dispatch, commit }, payload) => {
    commit('updateQuizStage', payload)
    const slideId = payload.id
    const slide = await dispatch('getSlideDataAction', slideId)
    const doc = {
      _from: 'live',
      type: 'slide',
      action: 'update',
      data: { ...slide, ...payload },
    }
    const { editor } = state
    const handler = getMessageSubscriber(doc.type)
    if (handler) {
      handler(doc, editor)
    }
  },
}

const mutations = {
  updateEditor: (state, editor) => {
    state.editor = editor
  },
  updateQuizStage: (state, payload) => {
    if (!payload.id) {
      return
    }

    state.quizStage[payload.id] = JSON.parse(JSON.stringify(payload))
  },
  updateSlideMap: (state, payload) => {
    if (payload.type == 'leaderboard') {
      return
    }
    if (!payload.id) {
      return
    }
    if (!state.slideMap[payload.id]) {
      state.slideMap[payload.id] = {}
    }
    state.slideMap[payload.id] = { ...state.slideMap[payload.id], ...payload }
  },

  clearSlideMap: (state) => {
    state.slideMap = {}
  },

  setSlideList: (state, slideList) => {
    state.slideList = slideList.map((item) => item.id)
  },

  setActiveSlideId: (state, slideId) => {
    state.activeSlideId = slideId
  },

  setPresenting: (state, status) => {
    state.isPresenting = status
  },
}

const mutationHandler = {
  setSlides: ({ store, mutation }) => {
    store.commit('nextback/setSlideList', mutation.payload)
    store.dispatch('nextback/prefetchNextSlideAction')
    const presentationId = store.state.presentation.id
    if (!presentationId) {
      throw Error('PresentationId is null or undefined, fetching sequene might be wrong')
    }
  },
  setPresentation: ({ store, mutation }) => {
    store.commit('nextback/setActiveSlideId', mutation.payload.activeSlide)
    const presentationId = mutation.payload.id
    store.dispatch('nextback/syncQuizStageAction', presentationId)
    store.dispatch('nextback/syncActiveSlideAction', presentationId)
    store.commit(NEXTBACK_SET_PRESENTING, mutation.payload.presenting)
  },
}

const actionHandler = {
  'kvSync/updateKeyValueAction': ({ action, store }) => {
    const { key, value } = action.payload
    if (key.includes('/quizStage/')) {
      store.dispatch('nextback/changeQuizStageAction', JSON.parse(value))
    }
    if (key.includes('/activeSlide/')) {
      store.dispatch('nextback/nextSlideActionV2', { message: JSON.parse(value) })
    }
  },
  'websocket/newMessage': ({ action, store }) => {
    /**
     * Since we ignore next slide command when not presenting, audience will not follow presenter
     * when editing in editor. When they click `present`, active slide
     * must be synced again
     */
    const update = action.payload
    if (update.type === 'presentation' && update.action === 'presentation-update') {
      if (update.data.presenting === 'false') {
        store.commit('nextback/clearSlideMap')
        store.commit(NEXTBACK_SET_PRESENTING, false)
      }
    }
    if (update.type === 'presentation' && update.action === 'presenting') {
      store.commit(NEXTBACK_SET_PRESENTING, true)
      store.dispatch('nextback/moveToSlideAction', { slideId: update.data.id })
    }
  },
}

const plugin =
  (store) => {
    store.subscribe((mutation) => {
      if (!store.getters['featureToggles/canUseNewChangeSlideMechanism']) {
        return
      }
      if (mutation.type in mutationHandler) {
        mutationHandler[mutation.type]({ store, mutation })
      }
    })

    store.subscribeAction((action) => {
      if (!store.getters['featureToggles/canUseNewChangeSlideMechanism']) {
        return
      }
      if (action.type in actionHandler) {
        actionHandler[action.type]({ store, action })
      }
    })
  }

export default {
  namespaced: true,
  state: _state,
  actions,
  mutations,
  plugin,
}
